import { inject, Injectable } from '@angular/core';
import {
  FeatureConfigService,
  OAUTH_REDIRECT_FLOW_KEY,
  OAuthLibWrapperService,
  OAuthTryLoginResult,
  RoutingService,
} from '@spartacus/core';
import { AsmModeService } from 'src/app/spartacus/features/asm/asm-mode.service';
import { ShimanoAuthRedirectService } from '../auth/shimano-auth-redirect.service';
import { StorageService } from '../storage/storage.service';
import { LoginAuthorizeService } from './login-authorize.service';
import { LoginService } from './login.service';

@Injectable()
export class CustomOAuthLibWrapperService extends OAuthLibWrapperService {
  private storageService = inject(StorageService);
  private routingService = inject(RoutingService);
  private loginService = inject(LoginService);
  private asmModeService = inject(AsmModeService);
  private loginAuthorizeService = inject(LoginAuthorizeService);
  private shimanoAuthRedirectService = inject(ShimanoAuthRedirectService);
  private featureConfigServiceCustom = inject(FeatureConfigService);

  /**
   * Dynamic getter — always evaluates the current ASM state.
   * Replaces the previous stale field initialization.
   */
  private get asmEnabled(): boolean {
    return this.asmModeService.isAsmMode();
  }

  //  Starts the OAuth authorization flow and applies the dynamic site-context redirect URI.
  override initLoginFlow() {
    const currentClientId = this.authConfigService.getClientId();
    const loginFlowStartedFor = this.storageService.getSessionStorageItem('loginFlowStartedFor');
    const hasValidtoken = !!this.oAuthService.getAccessToken();

    /**
     * Prevent duplicate /authorize for the same client_id.
     */
    if (loginFlowStartedFor === currentClientId && hasValidtoken) {
      console.warn('[Login] Skipping duplicate initLoginFlow for same clientId', currentClientId);
      return;
    }

    this.storageService.setSessionStorageItem('loginFlowStartedFor', currentClientId);

    const { businessType, country, lang } = this.getContextFromSessionStorage();
    this.configureDynamicOAuth();
    this.oAuthService.customQueryParams = {
      businessType,
      country,
      language: lang,
    };

    if (
      !this.featureConfigServiceCustom.isEnabled('authorizationCodeFlowByDefault') ||
      this.federatedLoginService.enabled
    ) {
      this.winRef.localStorage?.setItem(OAUTH_REDIRECT_FLOW_KEY, 'true');
    }
    return this.oAuthService.initLoginFlow();
  }

  // Processes the OAuth callback, stores the token, and handles post-login redirects.
  override tryLogin(): Promise<OAuthTryLoginResult> {
    this.configureDynamicOAuth();

    return super.tryLogin().then((result) => {
      const hasToken = !!this.oAuthService.getAccessToken();
      const isDealerLoginInProgress = this.storageService.getSessionStorageItem('dealerLoginInProgress') === 'true';
      const isAsmLoginInProgress = this.storageService.getSessionStorageItem('asmLoginInProgress') === 'true';
      const isLoginUrl = this.isRouteLogin(this.currentPathname);

      if (!hasToken) {
        if (isLoginUrl) {
          // re-trigger whenc changing to another country or business type
          //this.loginAuthorizeService.reAuthorize(() => this.initLoginFlow()); <-- do not trigger as possible cause of double reload
        }
        return result;
      }

      // Restore the original login page context after authorization completes
      // For ASM, this also supports redirect handling after authorization.
      if (isAsmLoginInProgress || isDealerLoginInProgress) {
        const loginContextRaw = sessionStorage.getItem('loginUrlContext');
        const loginUrlContext = loginContextRaw ? JSON.parse(loginContextRaw) : null;
        if (loginUrlContext) {
          this.loginService.setOAuthRedirectUri(
            loginUrlContext.businessType,
            loginUrlContext.country,
            loginUrlContext.language
          );
          sessionStorage.removeItem('loginUrlContext');
        }
        this.configureDynamicOAuth();
      }

      if (this.asmEnabled) {
        // Only redirect to dealer selection when tryLogin() is handling the ASM login callback.
        // This prevents unwanted redirects to dealer selection during reloads or impersonation navigation.
        if (isAsmLoginInProgress) {
          this.storageService.removeSessionStorageItem('asmLoginInProgress');
          this.storageService.setSessionStorageItem('pageLoadedFromLogin', 'true');
          this.storageService.removeSessionStorageItem('loginFlowStartedFor');
          this.redirectToDealerSelection();
          return result;
        }
      } else {
        // Only mark dealer login as completed when tryLogin() is handling the dealer login callback.
        // This prevents setting fromLoggedIn during unrelated tryLogin() calls, such as page reloads.
        if (isDealerLoginInProgress) {
          this.storageService.removeSessionStorageItem('standardLoginTriggered');
          this.storageService.removeSessionStorageItem('dealerLoginInProgress');
          this.storageService.removeSessionStorageItem('loginFlowStartedFor');
          this.storageService.setLocalStorageitem('fromLoggedIn', 'true');
          this.shimanoAuthRedirectService.redirect();
          return result;
        }
      }

      if (this.winRef.isBrowser()) {
        const isCallbackRoute = this.winRef.location.pathname.endsWith('/callback');
        const hasOAuthParams =
          this.winRef.location.search.includes('code=') || this.winRef.location.search.includes('error=');
        // Handle stale /callback pages caused by browser refresh after login.
        if (hasToken && isCallbackRoute && !hasOAuthParams) {
          if (this.asmEnabled) {
            this.redirectToDealerSelection();
          } else {
            this.shimanoAuthRedirectService.redirect();
          }
          return result;
        }
      }

      return result;
    });
  }

  private redirectToDealerSelection(): void {
    const dealerSelectionUrl = this.getDealerSelectionUrl();
    const lastAuthTime = this.storageService.getLocalStorageItem('lastAuthTime');
    const fiftyMinutes = 50 * 60 * 1000;

    const timeSinceLastAuth = lastAuthTime ? Date.now() - parseInt(lastAuthTime, 10) : 0;

    if (timeSinceLastAuth > fiftyMinutes) {
      window.location.href = dealerSelectionUrl;
      return;
    }
    this.routingService.goByUrl(dealerSelectionUrl);
  }

  private getDealerSelectionUrl(): string {
    const { businessType, country, lang } = this.getContextFromSessionStorage();

    if (businessType && country && lang) {
      return `/${businessType}/${country}/${lang}/dealer-selection`;
    }

    return '/dealer-selection';
  }

  // Re-applies the dynamic OAuth config when Spartacus refreshes auth settings.
  override refreshAuthConfig() {
    this.configureDynamicOAuth();
  }

  private getContextFromSessionStorage() {
    const businessType = this.storageService.getSessionStorageItem('ShimanoBusinessType') || '';
    const country = this.storageService.getSessionStorageItem('ShimanoCountry') || '';
    const lang = this.storageService.getSessionStorageItem('ShimanoLanguage') || '';

    return { businessType, country, lang };
  }

  private getDynamicRedirectUri(): string {
    const isSSR = !this.winRef.isBrowser();
    const { businessType, country, lang } = this.getContextFromSessionStorage();

    const baseOrigin = !isSSR ? this.winRef.location.origin : '';

    if (businessType && country && lang) {
      return `${baseOrigin}/${businessType}/${country}/${lang}/callback`;
    }

    return this.authConfigService.getOAuthLibConfig()?.redirectUri ?? baseOrigin;
  }

  // Applies OAuth configuration with the current dynamic redirect URI.
  configureDynamicOAuth(): void {
    super.refreshAuthConfig();
    const redirectUri = this.getDynamicRedirectUri();

    this.oAuthService.configure({
      tokenEndpoint: this.authConfigService.getTokenEndpoint(),
      loginUrl: this.authConfigService.getLoginUrl(),
      clientId: this.authConfigService.getClientId(),
      dummyClientSecret: this.authConfigService.getClientSecret(),
      revocationEndpoint: this.authConfigService.getRevokeEndpoint(),
      logoutUrl: this.authConfigService.getLogoutUrl(),
      userinfoEndpoint: this.authConfigService.getUserinfoEndpoint(),
      issuer: this.authConfigService.getOAuthLibConfig()?.issuer ?? this.authConfigService.getBaseUrl(),
      ...this.authConfigService.getOAuthLibConfig(),
      redirectUri,
    });
  }

  private get currentPathname(): string {
    if (!this.winRef.isBrowser()) {
      return '';
    }
    return this.winRef.location?.pathname ?? '';
  }

  private isRouteLogin(pathname: string): boolean {
    const relativePath = pathname.replace(/^\/(?:[^/]+\/[^/]+\/[^/]+\/)?/, '').replace(/\/$/, '');
    return relativePath === 'login';
  }
}
